import { useCallback } from 'react';
import type { z } from 'zod';
import type { RxCollection } from 'rxdb';
import {
    type GridColDef,
    type GridPreProcessEditCellProps,
    type GridRenderEditCellParams,
    type GridValidRowModel
} from '../lib/dataGrid';
import { useValidationMessage } from '../i18n/validationMessages';
import { useSavedToast } from './toastContext';
import ValidatedGridEditCell from './ValidatedGridEditCell';
import GridAutocompleteEditCell from './GridAutocompleteEditCell';
import GridMultiAutocompleteEditCell from './GridMultiAutocompleteEditCell';
import GridMultilineEditCell from './GridMultilineEditCell';
import GridPersonEditCell from './GridPersonEditCell';

/**
 * Editing a single cell in place, validated the way the FORM validates — the
 * per-field validator, the `{editable, renderEditCell, preProcessEditCellProps}`
 * triple and the `processRowUpdate`, written once instead of per grid.
 *
 * The validation is the point. `schema.shape[field]` is the field's rule ALONE,
 * so a rule spanning two fields (`pauseExceedsWork`, `startAfterEnd`) silently
 * did not reach a cell. Here the CANDIDATE ROW is parsed — the row as it would
 * be after the edit — and the issues pointing at this field are kept, which is
 * exactly what the form would put under that input.
 *
 * A cell still cannot see a rule that needs OTHER records (number uniqueness,
 * duplicate file names); those are separate hooks reading the collection.
 */

/**
 * The rule the edited value breaks, as the shared schemas' code ('maxLength',
 * 'pauseExceedsWork', …), or undefined when the cell is fine. The substance of
 * the module; the hook below only wraps it in a column and translates the code.
 *
 * Parses the row and the value being typed TOGETHER, which is what lets a rule
 * about a pair of fields reach one cell. Issues on OTHER fields are dropped: the
 * row already held them, and a cell may only object to what it is handed.
 */
export function inlineFieldIssue(
    schema: z.ZodType<unknown>,
    field: string,
    row: Record<string, unknown>,
    value: unknown
): string | undefined {
    const parsed = schema.safeParse({ ...row, [field]: value });
    if (parsed.success) {
        return undefined;
    }
    return parsed.error.issues.find((issue) => issue.path[0] === field)
        ?.message;
}

export type InlineEditor =
    | { kind: 'text' }
    /** Text the form writes over several lines — the form's `richText`. */
    | { kind: 'multiline' }
    /**
     * Free text with a vocabulary offered while typing — the form's `suggest`.
     * With `grouped`, the list comes from the record instead, each option under
     * a heading (the material tree: every name under its group, the record's
     * own group first) and `options` stays the plain vocabulary.
     */
    | {
          kind: 'suggest';
          options: readonly string[];
          grouped?: (
              record: Record<string, unknown>
          ) => readonly { value: string; group: string }[];
      }
    /** Chips over a ', '-joined value — the form's `multiSelectJoined`. */
    | { kind: 'multi'; options: readonly string[] }
    /**
     * A person, picked from the app's accounts — the form's `person`. Brings
     * its own options (the accounts, plus what the row already says), so unlike
     * the two above it takes none.
     */
    | { kind: 'person'; multiple?: boolean; leads?: boolean };

/** One field a grid may edit in place, with what its value carries along. */
export interface InlineField {
    field: string;
    editor?: InlineEditor;
    /**
     * The STORED value behind what the cell shows, where the two differ — a
     * Zeichner is a `string[]` shown ', '-joined, a Massstab is stored as
     * `M 1:50` however it was typed. The one place that conversion happens,
     * applied to what is WRITTEN ({@link inlinePatch}) and to what is VALIDATED.
     *
     * Must be idempotent: a row update writes every editable field, so this sees
     * edited text on one and already-stored values on the rest.
     */
    parse?: (value: unknown) => unknown;
    /**
     * The last values this field held, newest first — the F2 entry. The
     * suggestions above are alphabetical, but what a person reaches for is the
     * value from a moment ago. See ./recentValues and ./recentValueEntry;
     * absent, F2 does nothing.
     */
    recent?: readonly string[];
    /**
     * Fields DERIVED from this one, written in the same patch — a person's name
     * is the case it exists for: the record stores the name AND the account it
     * resolves to (`excavator` / `excavatorId`), and a cell writing only the name
     * would leave a stale link that every count downstream believes.
     *
     * Sees the value AFTER `parse`, so a list-valued field derives from the list
     * and not from its joined text. Runs only where the value actually CHANGED
     * (see {@link inlinePatch}), and gets the record as it stands with it, which
     * is what a LIST-valued field needs: its cell commits the whole list, so
     * entries that stayed keep their links (`resolveKeepingStored`).
     */
    derive?: (
        value: unknown,
        stored?: Record<string, unknown>
    ) => Record<string, unknown>;
    /**
     * Whether this record's cell is editable at all, where that depends on the
     * record — a Fund's Probenart only in the Fundgruppe "Proben", as the form
     * shows the field only there. Absent: every row's cell is.
     */
    editableWhen?: (record: Record<string, unknown>) => boolean;
}

/**
 * Build the column properties that make a field editable in place, or nothing at
 * all when the user may not edit — an uneditable column is the same column, so
 * the caller spreads the result either way and never branches.
 */
export function useInlineEditColumn<Row extends GridValidRowModel>(
    schema: z.ZodType<unknown>,
    canEdit: boolean,
    /**
     * The record behind a grid row, where the two differ. The Befund tree's rows
     * are a display shape carrying their document alongside; validating the
     * display shape would judge the value against a half-filled object.
     */
    docOf: (row: GridValidRowModel) => Record<string, unknown> = (row) => row
): (
    field: string,
    spec?: Pick<InlineField, 'editor' | 'parse' | 'recent'>
) => Partial<GridColDef<Row>> {
    const validationMessage = useValidationMessage();

    return useCallback(
        (
            field: string,
            spec: Pick<InlineField, 'editor' | 'parse' | 'recent'> = {}
        ) => {
            if (!canEdit) {
                return {};
            }
            const editor = spec.editor ?? { kind: 'text' };

            const preProcessEditCellProps = (
                params: GridPreProcessEditCellProps
            ) => {
                const issue = inlineFieldIssue(
                    schema,
                    field,
                    docOf(params.row),
                    // Judged as it would be STORED: a Zeichner cell holds one
                    // joined string, and the schema knows only the list.
                    spec.parse
                        ? spec.parse(params.props.value)
                        : params.props.value
                );
                return {
                    ...params.props,
                    error: issue ? validationMessage(issue) : null
                };
            };

            const renderEditCell = (params: GridRenderEditCellParams) => {
                if (editor.kind === 'suggest') {
                    const grouped = editor.grouped?.(docOf(params.row));
                    const groupOf = grouped
                        ? new Map(
                              grouped.map((option) => [
                                  option.value,
                                  option.group
                              ])
                          )
                        : null;
                    return (
                        <GridAutocompleteEditCell
                            {...params}
                            options={
                                grouped
                                    ? grouped.map((option) => option.value)
                                    : [...editor.options]
                            }
                            groupOf={
                                groupOf
                                    ? (option) => groupOf.get(option) ?? ''
                                    : undefined
                            }
                            recent={spec.recent}
                        />
                    );
                }
                if (editor.kind === 'person') {
                    return (
                        <GridPersonEditCell
                            {...params}
                            multiple={editor.multiple}
                            leads={editor.leads}
                            recent={spec.recent}
                            // Every record with a person field carries its
                            // Aufnahmedatum; read off the record, not the row,
                            // which may wrap it (see `docOf`).
                            day={docOf(params.row).recordedAt}
                        />
                    );
                }
                if (editor.kind === 'multiline') {
                    return <GridMultilineEditCell {...params} />;
                }
                if (editor.kind === 'multi') {
                    return (
                        <GridMultiAutocompleteEditCell
                            {...params}
                            options={[...editor.options]}
                        />
                    );
                }
                return (
                    <ValidatedGridEditCell {...params} recent={spec.recent} />
                );
            };

            return { editable: true, renderEditCell, preProcessEditCellProps };
        },
        [canEdit, schema, validationMessage, docOf]
    );
}

/** Same value as the record holds it — arrays by value, everything else by ===. */
function sameStoredValue(value: unknown, stored: unknown): boolean {
    if (Array.isArray(value) && Array.isArray(stored)) {
        return (
            value.length === stored.length &&
            value.every((item, index) => item === stored[index])
        );
    }
    return value === stored;
}

/**
 * What a committed row writes: the editable fields, trimmed, plus whatever each
 * carries along. Pure, so the rule can be tested without a database.
 *
 * `stored` is the record as it stands. A row commit runs EVERY field's `derive`,
 * so without it, correcting a Tagebuch's Wetter would re-derive its author link
 * from a name nobody touched — and a name the resolver cannot place any more
 * comes back empty and drops the account. So an unchanged value derives nothing.
 * Omitted, every derive runs: the Sammelbearbeitung writes ONE patch to many
 * records, where a deliberately typed value must resolve for all of them.
 */
export function inlinePatch(
    fields: readonly InlineField[],
    row: Record<string, unknown>,
    stored?: Record<string, unknown>
): Record<string, unknown> {
    const patch: Record<string, unknown> = {};
    for (const { field, derive, parse } of fields) {
        const value = inlineStoredValue(row[field], parse);
        patch[field] = value;
        const unchanged = stored && sameStoredValue(value, stored[field]);
        if (derive && !unchanged) {
            Object.assign(patch, derive(value, stored));
        }
    }
    return patch;
}

/**
 * One edited value as the record stores it: through the field's `parse` where it
 * has one, else trimmed like the form does on save — a name with a trailing
 * space must not become a second identity. A `parse` gets the raw value and does
 * its own tidying, since "what counts as empty" differs per shape.
 */
function inlineStoredValue(raw: unknown, parse: InlineField['parse']): unknown {
    if (parse) {
        return parse(raw);
    }
    return typeof raw === 'string' ? raw.trim() : raw;
}

/**
 * The dialog's values as the records would store them — what the
 * Sammelbearbeitung validates before it writes. `bulkFieldIssues` parses them
 * INTO the records, so a list-valued field must arrive as a list or every
 * record is judged against a string it would never hold.
 */
export function inlineStoredValues(
    fields: readonly InlineField[],
    values: Record<string, unknown>
): Record<string, unknown> {
    return Object.fromEntries(
        Object.entries(values).map(([field, value]) => [
            field,
            inlineStoredValue(
                value,
                fields.find((spec) => spec.field === field)?.parse
            )
        ])
    );
}

export interface BulkResult {
    /** Records actually patched. */
    changed: number;
    /**
     * Ids that were gone by the time the write reached them — deleted, or never
     * replicated to this device. Reported rather than thrown: the other records
     * were changed, and the count is the honest answer.
     */
    missing: number;
}

/**
 * The rules the SELECTED records break if `values` is written to all of them, as
 * `{field, code, count}`. Every record is parsed with the new value in place, as
 * a single cell is ({@link inlineFieldIssue}), because a rule may span two fields
 * and the other differs per record. The COUNT is what the dialog needs: the
 * answer to "3 of 23 would break" is to narrow the selection.
 */
export function bulkFieldIssues(
    schema: z.ZodType<unknown>,
    values: Record<string, unknown>,
    records: readonly Record<string, unknown>[]
): { field: string; code: string; count: number }[] {
    const counts = new Map<string, { code: string; count: number }>();
    for (const [field, value] of Object.entries(values)) {
        for (const record of records) {
            const code = inlineFieldIssue(schema, field, record, value);
            if (!code) {
                continue;
            }
            const seen = counts.get(field);
            counts.set(field, {
                code: seen?.code ?? code,
                count: (seen?.count ?? 0) + 1
            });
        }
    }
    return [...counts].map(([field, entry]) => ({ field, ...entry }));
}

/**
 * How many records are patched at once. Each patch is its own write and its own
 * push to the server, so a whole grid ticked at once is hundreds of them; a
 * ceiling keeps that from arriving as one burst.
 */
const BULK_CONCURRENCY = 8;

/**
 * Write ONE set of field values to MANY records — the Sammelbearbeitung. The
 * patch is built once and reused, since `derive` hangs off the VALUE rather than
 * the record. Only the fields named in `values` are written; an untouched field
 * is not cleared.
 *
 * `incrementalPatch` per document rather than a bulk write: it re-reads and
 * re-applies on conflict, which is what keeps this safe against someone else
 * having touched the record. One failure does not stop the rest.
 */
export function useBulkFieldUpdate<Row extends GridValidRowModel>(
    collection: RxCollection<Row> | null,
    fields: readonly InlineField[]
): (
    ids: readonly string[],
    values: Record<string, unknown>
) => Promise<BulkResult> {
    return useCallback(
        async (ids, values) => {
            if (!collection || ids.length === 0) {
                return { changed: 0, missing: 0 };
            }
            const touched = fields.filter((spec) => spec.field in values);
            const patch = {
                ...inlinePatch(touched, values),
                updatedAt: new Date().toISOString()
            };
            let changed = 0;
            let missing = 0;
            let next = 0;
            const workers = Array.from(
                { length: Math.min(BULK_CONCURRENCY, ids.length) },
                async () => {
                    while (next < ids.length) {
                        const id = ids[next];
                        next += 1;
                        const doc = await collection.findOne(id).exec();
                        if (!doc) {
                            missing += 1;
                            continue;
                        }
                        // Same cast as the single-row path: the patch is keyed
                        // by field name at runtime, which a list of names
                        // cannot prove to RxDB's document type.
                        await doc.incrementalPatch(
                            patch as unknown as Partial<Row>
                        );
                        changed += 1;
                    }
                }
            );
            await Promise.all(workers);
            return { changed, missing };
        },
        [collection, fields]
    );
}

/**
 * The `processRowUpdate` behind those cells: patch the fields that may be edited
 * in place and nothing else. A grid row carries computed columns and fields the
 * form owns, so patching it wholesale is how an inline edit quietly reverts
 * something edited in the form a moment earlier.
 */

export function useInlineRowUpdate<Row extends GridValidRowModel>(
    collection: RxCollection<Row> | null,
    fields: readonly InlineField[]
): (newRow: Row) => Promise<Row> {
    const notifySaved = useSavedToast();

    return useCallback(
        async (newRow: Row) => {
            const doc = await collection?.findOne(newRow.id as string).exec();
            if (!doc) {
                return newRow;
            }
            const patch = {
                // With the document as it stands: an untouched name keeps the
                // account link it already carries (see inlinePatch).
                ...inlinePatch(
                    fields,
                    newRow,
                    doc.toJSON() as unknown as Record<string, unknown>
                ),
                updatedAt: new Date().toISOString()
            };
            // The patch is keyed by field name at runtime; RxDB types it against
            // the doc type, which a list of field names cannot prove.
            await doc.incrementalPatch(patch as unknown as Partial<Row>);
            notifySaved();
            // The PATCHED row back to the grid, not the edited one: the cell
            // handed over text, and a column that reads a list off the row
            // (`drafter.join(', ')`) would be handed a string until the next
            // sync emission caught up.
            return { ...newRow, ...patch };
        },
        [collection, fields, notifySaved]
    );
}
